import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TokenRequest, TokenResource } from "@sarva/api-client";
import { exchangeDevToken } from "../api/dev-token";

/**
 * doc 27 §5: server access goes through a Query hook, mutations included.
 *
 * No `Idempotency-Key` here, and that is not an oversight: doc 22 §6 requires
 * one on mutations that change data. A token exchange creates a session, and
 * issuing two sessions for one sign-in is not a double-charge — it is a second
 * login. The idempotency requirement exists to stop a retried write happening
 * twice, and the API does not require a key on this route.
 */
export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation<TokenResource, unknown, TokenRequest>({
    mutationFn: (request) => exchangeDevToken(request),
    // D239 opt-out: the sign-in page answers every outcome itself — a code request is not a
    // failure, and a wrong password has its own wording.
    meta: { announceError: false },
    onSuccess: () => {
      // A new identity invalidates everything the previous one cached. Removing
      // rather than invalidating: a stale row from another session must never
      // be painted under the new one's header while a refetch is in flight.
      queryClient.clear();
    },
  });
}
